import * as uuid from 'uuid';
import _ from 'lodash';
import moment from 'moment';

import styles from './utils.module.css';

import * as api from '../api';

import { InfoMessage } from './InfoMessage';
import { List } from './List';
import { isNumberValid } from '../utils/phoneNumber';
import { FormFieldWithConfig } from '../reducers/store';
import { fieldScoreUpTo } from '../utils/form/condition/score';
import * as Template from '../utils/template';

///////////////////////////////////////////////////
////// Types and Const

const { Primitive } = api.PrevisitFormTemplateFieldConfig;

const REGEX_A = /\[(.*?)\]\((.*?)\)/gm;
const REGEX_UL = /\n([-|*][\W\w]*?(?=\n\n|$))/g;
const REGEX_LI = /^[-|*]+(.*?)$/gm;
const REGEX_INFO = /\[info\]([\w\W]*?)\[\/info\]/g;
const REGEX_ALERT = /\[alert\]([\w\W]*?)\[\/alert\]/g;

type Parsed = (string | JSX.Element)[];

export interface Option<T> {
  display: string;
  key: string;
  value: T;
}

export enum FormatOption {
  Alert,
  Info,
  UnnumberedList,
  Links,
}

type FormatType = {
  [key in FormatOption]: (input: Parsed) => Parsed;
};

// ORDER MATTERS
const ALL_FORMATS = [
  FormatOption.Alert,
  FormatOption.Info,
  FormatOption.UnnumberedList,
  FormatOption.Links,
];

const FORMAT_MAP: FormatType = {
  [FormatOption.Alert]: formatAlert,
  [FormatOption.Info]: formatInfo,
  [FormatOption.UnnumberedList]: formatUnnumberedList,
  [FormatOption.Links]: formatLinks,
};

const PRIMITIVE_TO_PATIENT_KEY_MAP: {
  [value in api.PrevisitFormTemplateFieldConfig.Primitive]?: keyof api.Patient.Patient;
} = {
  [api.PrevisitFormTemplateFieldConfig.Primitive.PatientCellPhone]:
    'cellPhoneNumber',
  [api.PrevisitFormTemplateFieldConfig.Primitive.PatientPreferredFirstName]:
    'preferredFirstName',
  [api.PrevisitFormTemplateFieldConfig.Primitive.PatientDob]: 'dateOfBirth',
};

///////////////////////////////////////////////////
////// Formatting [alert][/alert]

function parseAlert(input: string): JSX.Element {
  return (
    <InfoMessage key={uuid.v4()} isError={true}>
      {formatString(input.trim(), [FormatOption.Alert, FormatOption.Info])}
    </InfoMessage>
  );
}

function formatUlAlert(input: string): (string | JSX.Element)[] {
  const matches = input.match(REGEX_ALERT);

  if (!matches || !matches?.length) return [input];

  const parsedUl = matches.reduce(
    (acc, match) => {
      const matchGroups = new RegExp(REGEX_ALERT).exec(match);

      if (!matchGroups) return acc;

      if (!acc.remaining) {
        acc.toReturn = acc.toReturn.concat([parseAlert(matchGroups[1])]);
        return acc;
      }

      const matchIndex = acc.remaining.indexOf(matchGroups[0]);

      const beforeMatch = acc.remaining.substring(0, matchIndex);
      const afterMatch = acc.remaining.substring(
        matchIndex + matchGroups[0].length,
      );

      acc.remaining = afterMatch;
      acc.toReturn = acc.toReturn.concat([
        beforeMatch,
        parseAlert(matchGroups[1]),
      ]);

      if (!afterMatch?.match(REGEX_ALERT))
        acc.toReturn = acc.toReturn.concat([afterMatch]);

      return acc;
    },
    {
      remaining: input,
      toReturn: [] as Parsed,
    },
  );

  return parsedUl.toReturn;
}

function formatAlert(input: Parsed): Parsed {
  return input.reduce((acc, cur) => {
    if (typeof cur !== 'string') {
      acc.push(cur);
    } else {
      acc.push(...formatUlAlert(cur));
    }
    return acc;
  }, [] as Parsed);
}

///////////////////////////////////////////////////
////// Formatting [info][/info]

function parseInfo(input: string): JSX.Element {
  return (
    <InfoMessage key={uuid.v4()}>
      {formatString(input.trim(), [FormatOption.Alert, FormatOption.Info])}
    </InfoMessage>
  );
}

function formatUlInfo(input: string): (string | JSX.Element)[] {
  const matches = input.match(REGEX_INFO);

  if (!matches || !matches?.length) return [input];

  const parsedUl = matches.reduce(
    (acc, match) => {
      const matchGroups = new RegExp(REGEX_INFO).exec(match);

      if (!matchGroups) return acc;

      if (!acc.remaining) {
        acc.toReturn = acc.toReturn.concat([parseInfo(matchGroups[1])]);
        return acc;
      }

      const matchIndex = acc.remaining.indexOf(matchGroups[0]);

      const beforeMatch = acc.remaining.substring(0, matchIndex);
      const afterMatch = acc.remaining.substring(
        matchIndex + matchGroups[0].length,
      );

      acc.remaining = afterMatch;
      acc.toReturn = acc.toReturn.concat([
        beforeMatch,
        parseInfo(matchGroups[1]),
      ]);

      if (!afterMatch?.match(REGEX_INFO))
        acc.toReturn = acc.toReturn.concat([afterMatch]);

      return acc;
    },
    {
      remaining: input,
      toReturn: [] as Parsed,
    },
  );

  return parsedUl.toReturn;
}

function formatInfo(input: Parsed): Parsed {
  return input.reduce((acc, cur) => {
    if (typeof cur !== 'string') {
      acc.push(cur);
    } else {
      acc.push(...formatUlInfo(cur));
    }
    return acc;
  }, [] as Parsed);
}

///////////////////////////////////////////////////
////// Formatting
////// - aaaa
////// - bbbb
////// - cccc

function parseUl(input: string): JSX.Element {
  const listItemMatches = input.match(REGEX_LI);

  if (!listItemMatches) return <></>;

  const ulOptions = listItemMatches.map((listItemMatch) => {
    const listItemValues = new RegExp(REGEX_LI).exec(listItemMatch);

    return (listItemValues && listItemValues[1]) || '';
  });

  return <List key={uuid.v4()} listItems={ulOptions.filter((x) => !!x)} />;
}

function formatUl(input: string): (string | JSX.Element)[] {
  const matches = input.match(REGEX_UL);

  if (!matches || !matches?.length) return [input];

  const parsedUl = matches.reduce(
    (acc, match) => {
      const matchGroups = new RegExp(REGEX_UL).exec(match);

      const matchString = matchGroups && matchGroups[1];

      if (!matchString) return acc;

      if (!acc.remaining) {
        acc.toReturn = acc.toReturn.concat([parseUl(matchString)]);
        return acc;
      }

      const matchIndex = acc.remaining.indexOf(matchString);

      const beforeMatch = acc.remaining.substring(0, matchIndex);
      const afterMatch = acc.remaining.substring(
        matchIndex + matchString.length,
      );

      const toAdd = beforeMatch
        ? [beforeMatch, parseUl(matchString)]
        : [parseUl(matchString), afterMatch];

      acc.remaining = afterMatch;
      acc.toReturn = acc.toReturn.concat(toAdd);

      if (!afterMatch?.match(REGEX_UL))
        acc.toReturn = acc.toReturn.concat([afterMatch]);

      return acc;
    },
    {
      remaining: input,
      toReturn: [] as Parsed,
    },
  );

  return parsedUl.toReturn;
}

function formatUnnumberedList(input: Parsed): Parsed {
  return input.reduce((acc, cur) => {
    if (typeof cur !== 'string') {
      acc.push(cur);
    } else {
      acc.push(...formatUl(cur));
    }
    return acc;
  }, [] as Parsed);
}

///////////////////////////////////////////////////
////// Formatting [display](url)

function parseAnchor(input: RegExpMatchArray): JSX.Element {
  return (
    <a
      className={styles.TextLink}
      key={uuid.v4()}
      href={input[2]}
      target="_blank"
      rel="noopener noreferrer"
    >
      {input[1]}
    </a>
  );
}

function formatAnchor(input: string): (string | JSX.Element)[] {
  const matches = input.match(REGEX_A);

  if (!matches || !matches?.length) return [input];

  const parsedAnchor = matches.reduce(
    (acc, match) => {
      const matchGroups = new RegExp(REGEX_A).exec(match);

      if (!matchGroups) return acc;

      if (!acc.remaining) {
        acc.toReturn = acc.toReturn.concat([parseAnchor(matchGroups)]);
        return acc;
      }

      const matchIndex = acc.remaining.indexOf(match);

      const beforeMatch = acc.remaining.substring(0, matchIndex);
      const afterMatch = acc.remaining.substring(matchIndex + match.length);

      acc.remaining = afterMatch;
      acc.toReturn = acc.toReturn.concat([
        beforeMatch,
        parseAnchor(matchGroups),
      ]);

      if (!afterMatch?.match(REGEX_A))
        acc.toReturn = acc.toReturn.concat([afterMatch]);

      return acc;
    },
    {
      remaining: input,
      toReturn: [] as Parsed,
    },
  );

  return parsedAnchor.toReturn;
}

function formatLinks(input: Parsed): Parsed {
  return input.reduce((acc, cur) => {
    if (typeof cur !== 'string') {
      acc.push(cur);
    } else {
      acc.push(...formatAnchor(cur));
    }
    return acc;
  }, [] as Parsed);
}

///////////////////////////////////////////////////
////// General formatting

const applyFormats = (input: string, formats: FormatOption[]): Parsed => {
  return formats.reduce((acc, format) => FORMAT_MAP[format](acc), [
    input,
  ] as Parsed);
};

function convertAllToElms(input: Parsed): JSX.Element[] {
  return input.reduce((acc, cur, index) => {
    if (typeof cur === 'string') {
      acc.push(<span key={index}>{cur}</span>);
    } else {
      acc.push(cur);
    }
    return acc;
  }, [] as JSX.Element[]);
}

///////////////////////////////////////////////////
////// Public

export function formatString(
  input: string,
  formatToSkip?: FormatOption[],
): JSX.Element[] {
  const formatsToApply =
    formatToSkip && formatToSkip.length
      ? ALL_FORMATS.filter((format) => !formatToSkip.includes(format))
      : ALL_FORMATS;

  const parsed: Parsed = applyFormats(input, formatsToApply);

  return convertAllToElms(parsed);
}

export function getOptionsIntersection<T>(
  optionEnum: Record<string, T>,
  options: T[],
): T[] {
  return _.intersection(Object.values(optionEnum), options);
}

export function getOptionsFromArray(
  values: string[],
  displayTransform?: (value: string) => string,
): Option<string>[] {
  return values.map((val) => ({
    display: displayTransform ? displayTransform(val) : val,
    key: val,
    value: val,
  }));
}

export function getOptionsFromRecord(
  values: Record<string, string>,
  displayTransform?: (value: string) => string,
): Option<string>[] {
  return _.map(values, (value, key) => ({
    display: displayTransform ? displayTransform(value) : value,
    label: displayTransform ? displayTransform(value) : value,
    key,
    value: key,
  }));
}

export const isStringArray = (
  arr: string[] | Option<string>[],
): arr is string[] => {
  return typeof arr[0] === 'string';
};

export function getPreferredPhoneOptions(
  fields: FormFieldWithConfig[],
  patient: api.Patient.Patient | null,
): { phoneNumber: string; kind: api.Common.PhoneNumberKind }[] {
  const options: {
    phoneNumber: string;
    kind: api.Common.PhoneNumberKind;
  }[] = [];
  const cellPhoneField = fields.find(
    (field) => field.primitive === Primitive.PatientCellPhone,
  );
  const homePhoneField = fields.find(
    (field) => field.primitive === Primitive.PatientHomePhone,
  );
  const workPhoneField = fields.find(
    (field) => field.primitive === Primitive.PatientWorkPhone,
  );
  const cellPhoneNumber = cellPhoneField
    ? cellPhoneField.value && isNumberValid(cellPhoneField.value)
      ? cellPhoneField.value
      : undefined
    : patient?.cellPhoneNumber;
  const homePhoneNumber = homePhoneField
    ? homePhoneField.value && isNumberValid(homePhoneField.value)
      ? homePhoneField.value
      : undefined
    : patient?.homePhoneNumber;
  const workPhoneNumber = workPhoneField
    ? workPhoneField.value && isNumberValid(workPhoneField.value)
      ? workPhoneField.value
      : undefined
    : patient?.workPhoneNumber;
  if (cellPhoneNumber) {
    options.push({
      phoneNumber: cellPhoneNumber,
      kind: api.Common.PhoneNumberKind.Cell,
    });
  }
  if (homePhoneNumber) {
    options.push({
      phoneNumber: homePhoneNumber,
      kind: api.Common.PhoneNumberKind.Home,
    });
  }
  if (workPhoneNumber) {
    options.push({
      phoneNumber: workPhoneNumber,
      kind: api.Common.PhoneNumberKind.Work,
    });
  }
  return options;
}

export function getValueFromFieldOrPatient(
  primitive: api.PrevisitFormTemplateFieldConfig.Primitive,
  fields: FormFieldWithConfig[],
  patient: api.Patient.Patient | null,
): string | undefined {
  const formField = fields.find((field) => field.primitive === primitive);
  if (formField?.value) {
    return formField.value;
  }
  const patientAttribute = PRIMITIVE_TO_PATIENT_KEY_MAP[primitive];
  if (!patient || !patientAttribute || !patient[patientAttribute]) return;
  return `${patient[patientAttribute]}`;
}

export function sliceUuid(value: string, start: number = -10): string {
  return value.slice(start);
}

export function retrieveReturnToClinicStartEndDays(args: {
  orderCreated: Date;
  scheduleAppointmentEndTime: api.PrevisitFormTemplateFieldConfig.TimeWithUnit | null;
  followUpValue?: number | null;
  followUpUnit?: string | null;
}): { startDays: number; endDays: number } {
  const {
    orderCreated,
    scheduleAppointmentEndTime,
    followUpValue,
    followUpUnit,
  } = args;
  let durationValue;
  if (followUpUnit) {
    switch (followUpUnit) {
      case 'day':
      case 'days':
      case 'week':
      case 'weeks':
      case 'month':
      case 'months':
      case 'year':
      case 'years':
        durationValue = followUpUnit;
        break;
      default:
        throw new Error('Follow up unit not supported.');
    }
  }
  // Add a default of 367 days afterwards
  const currentMoment = moment(new Date());
  const startMoment =
    followUpValue && durationValue
      ? moment(orderCreated).add(followUpValue, durationValue)
      : moment(orderCreated);
  const endMoment = scheduleAppointmentEndTime
    ? moment(startMoment.toDate()).add(
        scheduleAppointmentEndTime.time,
        api.PracticePreference.TimeUnitToMomentUnit[
          scheduleAppointmentEndTime.unit
        ],
      )
    : moment(startMoment.toDate()).add(367, 'days');
  let normalizeValue = 0;
  if (currentMoment > moment(orderCreated)) {
    normalizeValue = currentMoment.diff(orderCreated, 'days');
  } else {
    normalizeValue = currentMoment.diff(orderCreated, 'days') - 1;
  }
  return {
    startDays: startMoment.diff(orderCreated, 'days') - normalizeValue,
    endDays: endMoment.diff(orderCreated, 'days') - normalizeValue,
  };
}

export function getMaybeDaysFromTimingWithUnit(
  value: api.PrevisitFormTemplateFieldConfig.TimeWithUnit | null,
): number | null {
  if (!value) return null;
  switch (value.unit) {
    case api.Common.TimingUnit.Minute:
      return Math.floor(value.time / 60 / 24);
    case api.Common.TimingUnit.Hour:
      return Math.floor(value.time / 24);
    case api.Common.TimingUnit.Day:
      return value.time;
    default:
      return null;
  }
}

export const getPrimiveOptionValue = (
  optionsObj: api.Previsit.PrimitiveOptionTypes,
  value: string,
): string => {
  return (
    _.find(optionsObj, (optionValue, optionKey) => optionKey === value) || ''
  );
};

export const getKeyFromPrimitiveOption = (
  optionsObj: api.Previsit.PrimitiveOptionTypes,
  newValue: string,
): string =>
  _.findKey(optionsObj, (optionValue) => newValue === optionValue) || '';

export function isTruthy<T>(value: T | null | undefined | false): value is T {
  return !!value;
}

export const fullPagePrimitive = (
  primitive: api.PrevisitFormTemplateFieldConfig.Primitive,
): boolean => {
  // Return true when we have a primitive that should be the full-width of the page
  return [Primitive.ScheduleAppointment].includes(primitive);
};

export function compileLabelFromFieldOrPatient(args: {
  label: string;
  fields: api.PrevisitFormField.PrevisitFormFieldWithConfig[];
  patient: api.Patient.Patient | null;
  page: number;
  rank: number;
  extraParameters?: Record<string, string | null>;
}): string {
  const { label, fields, patient, page, rank, extraParameters } = args;
  const patientPreferredFirstName =
    getValueFromFieldOrPatient(
      Primitive.PatientPreferredFirstName,
      fields,
      patient,
    ) ??
    patient?.firstName ??
    null;
  const formScores = fieldScoreUpTo({ fields, page, rank });
  const totalScore = (
    formScores[api.PrevisitForm.DEFAULT_SCORE_TYPE] || 0
  ).toString();
  const parameters = {
    patientPreferredFirstName,
    formScore: totalScore,
    ...extraParameters,
  };
  return Template.applyTemplateParameters({
    template: label,
    parameters,
  });
}
